import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaArrowLeft, FaCog, FaPen } from "react-icons/fa";
import { IoIosWarning } from "react-icons/io";
import { useTranslation } from "../context/TranslationContext";
import {
  fetchClientMatterDetails,
  fetchClientPartyDetails,
  updateClientPartyAddress,
  updateClientPartyContact,
} from "../helpers/apiHelper";
import { canEditPartyAddress, isAddressEditEnabled } from "../helpers/addressHelper";
import { formatPhoneForDisplay } from "../helpers/contactEditHelper";
import {
  displayAddressField,
  displayValue,
  formatDisplayDate,
} from "../helpers/partyDetailsHelper";
import { formatDateToDDMMYYYY } from "../helpers/utilityHelper";
import { translateAlertMessage } from "../helpers/clientDisplayHelper";
import { classifyPartyAlert, partyFieldFlags } from "../helpers/partyAlertsHelper";
import { useFeatureFlags } from "../context/FeatureFlagsContext";
import { useAuth } from "../context/AuthContext";
import AddressEditModal from "./AddressEditModal";
import TaxIdChangeAction from "./TaxIdChangeAction";
import ContactEditModal from "./ContactEditModal";
import PartyAlertsBanner from "./PartyAlertsBanner";
import { CompensationTable } from "./DetailsLayout";
import "../styles/ClientDetails.css";
import "../styles/Views.css";
import "../styles/PartyDetails.css";

const ADDRESS_DISPLAY_FIELDS = ["street", "houseNumber", "complement", "neighborhood", "city", "state", "postalCode"];
// Country only shows once the backend sends it (with `addressUnmask`).
const OPTIONAL_ADDRESS_DISPLAY_FIELDS = ["country"];

// API Gateway caps a request at ~29s and fetchWithRetry retries a 504 once, so these bound the worst case.
export const PARTY_FETCH_TIMEOUT_MS = 35000;
export const MATTER_FETCH_TIMEOUT_MS = 35000;

const Field = ({ label, value, testId, flag }) => (
  <div className="party-field">
    <div className="party-field-label">{label}</div>
    <div className="party-field-value">
      <span data-testid={testId}>{value}</span>
      {flag}
    </div>
  </div>
);

const FieldFlag = ({ flag, field, label, t, onAdd }) => {
  if (!flag) return null;
  const level = flag.level.toLowerCase();
  const fullMessage = translateAlertMessage(flag.message, t);
  const isSuspicious = flag.kind === "suspicious";
  return (
    <span className="party-field-flags" data-testid={`party-flag-${field}`}>
      <span
        className={`party-field-flag is-${level}${isSuspicious ? " has-tooltip" : ""}`}
        data-level={level}
        data-tooltip={isSuspicious ? fullMessage : undefined}
        tabIndex={isSuspicious ? 0 : undefined}
      >
        <IoIosWarning aria-hidden="true" />
        {t(`clientDetails.party.alerts.${flag.kind}`)}
        {isSuspicious && <span className="sr-only">: {fullMessage}</span>}
      </span>
      {onAdd && (
        <button
          type="button"
          className="party-field-add"
          onClick={onAdd}
          aria-label={t("clientDetails.party.alerts.addField").replace("{field}", label)}
        >
          {t("clientDetails.party.alerts.add")}
        </button>
      )}
    </span>
  );
};

const StatusMessage = ({ message }) => (message ? (
  <div className={`status-message ${message.type === "error" ? "is-error" : "is-success"}`} role="status">
    {message.text}
  </div>
) : null);

const ClientDetailsParty = () => {
  const { t } = useTranslation();
  const { partyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tokens, user } = useAuth();
  const { features } = useFeatureFlags();
  const addressEditEnabled = isAddressEditEnabled(features);

  // matterId/matterName come from navigation state, but on reload or a direct link only ?matterId= survives.
  const matterId = location.state?.matterId || new URLSearchParams(location.search).get("matterId");
  const stateMatterName = location.state?.matterName;

  const [loadingParty, setLoadingParty] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [client, setClient] = useState(null);
  const [matterDetails, setMatterDetails] = useState(null);
  const [loadingMatter, setLoadingMatter] = useState(Boolean(matterId));
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [statusMessageContact, setStatusMessageContact] = useState(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [statusMessageAddress, setStatusMessageAddress] = useState(null);
  const [addressEditForbidden, setAddressEditForbidden] = useState(false);

  // Token refreshes must not refetch (and flash the loader), so effects read the latest token from a ref.
  const idToken = tokens?.idToken;
  const hasToken = Boolean(idToken);
  const tokenRef = useRef(idToken);
  tokenRef.current = idToken;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!partyId) {
      setLoadingParty(false);
      setLoadError(true);
      return undefined;
    }
    if (!hasToken) return undefined;

    let cancelled = false;
    let timedOut = false;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, PARTY_FETCH_TIMEOUT_MS);

    setLoadingParty(true);
    setLoadError(false);

    fetchClientPartyDetails(tokenRef.current, partyId, controller.signal)
      .then((party) => {
        if (cancelled) return;
        if (!party) throw new Error("No result returned");
        setClient(party);
        setLoadingParty(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (timedOut) {
          console.warn(`Timed out fetching party with ID: ${partyId}`);
          setLoadError(true);
          setLoadingParty(false);
          return;
        }
        console.warn(`Failed to fetch party with ID: ${partyId}`, err);
        navigateRef.current("/404", { replace: true });
      })
      .finally(() => clearTimeout(timer));

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [partyId, hasToken, reloadKey]);

  // The matter is secondary data: it loads independently so a slow lookup never blocks the party.
  useEffect(() => {
    if (!matterId || !hasToken) {
      setMatterDetails(null);
      setLoadingMatter(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), MATTER_FETCH_TIMEOUT_MS);
    setLoadingMatter(true);

    fetchClientMatterDetails(tokenRef.current, matterId, controller.signal)
      .then((result) => {
        if (!cancelled) setMatterDetails(result || null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn(`Failed to fetch matter with ID: ${matterId}`, error);
        setMatterDetails(null);
      })
      .finally(() => {
        clearTimeout(timer);
        if (!cancelled) setLoadingMatter(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [matterId, hasToken, reloadKey]);

  // Reloads the party after a CPF/CNPJ request without the full-page loader (the modal stays open).
  const refreshPartySilently = () => {
    if (!partyId || !tokenRef.current) return;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), PARTY_FETCH_TIMEOUT_MS);
    fetchClientPartyDetails(tokenRef.current, partyId, controller.signal, { redirectOnServerError: false })
      .then((party) => {
        if (party) setClient(party);
      })
      .catch((error) => console.warn(`Failed to refresh party with ID: ${partyId}`, error))
      .finally(() => clearTimeout(timer));
  };

  const clearSuccessMessage = (setter) => {
    setTimeout(() => setter(null), 3000);
  };

  // partycontactpatch accepts { email, phone } together and writes both in one Salesforce PATCH.
  const handleContactSave = async (changes) => {
    await updateClientPartyContact(partyId, changes, tokens.idToken);
    setClient((prev) => ({
      ...prev,
      details: { ...prev.details, ...changes },
      alerts: Array.isArray(prev.alerts)
        ? prev.alerts.filter((alert) => {
          const match = classifyPartyAlert(alert?.message);
          return !(match?.kind === "missing" && changes[match.field]);
        })
        : prev.alerts,
    }));
    setIsContactModalOpen(false);
    const successKey = changes.email && changes.phone
      ? "clientDetails.contact.updateSuccessBoth"
      : changes.phone ? "clientsTable.updateSuccessPhone" : "clientsTable.updateSuccessEmail";
    setStatusMessageContact({ type: "success", text: t(successKey) });
    clearSuccessMessage(setStatusMessageContact);
  };

  const handleAddressSave = async (address) => {
    let result;
    try {
      result = await updateClientPartyAddress(partyId, address, tokens.idToken, {
        currentDetails: client?.details,
      });
    } catch (error) {
      // The backend refuses non-owners; drop the action once it says so.
      if (error?.status === 403) setAddressEditForbidden(true);
      throw error;
    }
    setClient((prev) => ({
      ...prev,
      details: { ...prev.details, ...address, ...(result?.details || {}) },
    }));
    setIsAddressModalOpen(false);
    setStatusMessageAddress({ type: "success", text: t("clientDetails.address.success") });
    clearSuccessMessage(setStatusMessageAddress);
  };

  const currentAddress = useMemo(
    () => (client?.details
      ? ADDRESS_DISPLAY_FIELDS.reduce((acc, field) => ({ ...acc, [field]: client.details[field] }), {})
      : {}),
    [client?.details],
  );

  const handleBack = () => {
    // A direct link or reload has no in-app history entry to go back to.
    if (location.key === "default") {
      navigate("/");
      return;
    }
    navigate(-1);
  };

  const backLink = (
    <button type="button" className="party-back-link" onClick={handleBack}>
      <FaArrowLeft aria-hidden="true" />
      <span>{t("back")}</span>
    </button>
  );

  if (loadingParty || loadError) {
    return (
      <div className="portal-page client-details-container party-details">
        {backLink}
        <header className="portal-page-panel party-header-card">
          <p className="portal-page-kicker">{t("breadcrumb.partyDetails")}</p>
          {loadError ? (
            <div className="party-load-error" role="alert">
              <p>{t("clientDetails.party.loadError")}</p>
              <button type="button" className="portal-button" onClick={() => setReloadKey((key) => key + 1)}>
                {t("clientDetails.party.retry")}
              </button>
            </div>
          ) : (
            <p className="party-loading" role="status">
              <FaCog className="spinner-cog" aria-hidden="true" />
              {t("loading")}
            </p>
          )}
        </header>
      </div>
    );
  }

  const details = client.details || {};
  const canEditAddress = canEditPartyAddress(features, details, { forbidden: addressEditForbidden });
  const alerts = Array.isArray(client.alerts) ? client.alerts : [];
  const matter = matterDetails?.details;
  const matterName = stateMatterName || matter?.name;
  const masterSchedule = matter?.bhpMasterSchedule;
  const documentLabel = details.cpf || !details.cnpj ? t("clientsTable.cpf") : t("clientsTable.cnpj");
  const documentValue = details.cpf || details.cnpj;
  const compensationRecords = matterDetails?.compensation?.records || [];
  const fieldFlags = partyFieldFlags(alerts);
  const openContactModal = () => setIsContactModalOpen(true);
  const flagFor = (field, label, canAdd) => (
    <FieldFlag
      flag={fieldFlags[field]}
      field={field}
      label={label}
      t={t}
      onAdd={canAdd && fieldFlags[field]?.kind === "missing" ? openContactModal : undefined}
    />
  );

  return (
    <div className="portal-page client-details-container party-details">
      {backLink}

      <PartyAlertsBanner alerts={alerts} />

      <section className="portal-page-panel party-main-card" aria-labelledby="party-name-title">
        <header className="party-main-header">
          <p className="portal-page-kicker">{t("breadcrumb.partyDetails")}</p>
          <h2 className="party-name" id="party-name-title" data-testid="party-name">{displayValue(details.name)}</h2>
          {(masterSchedule || (matterId && (matterName || loadingMatter))) && (
            <ul className="party-chips">
              {matterId && matterName && (
                <li className="party-chip is-link" data-testid="party-chip-matter">
                  <span className="party-chip-label">{t("clientsTable.matterName")}</span>
                  <button
                    type="button"
                    className="party-chip-link"
                    onClick={() => navigate(
                      `/client-details-matter/${encodeURIComponent(matterId)}`,
                      { state: { partyId, matterName } },
                    )}
                  >
                    {matterName}
                  </button>
                </li>
              )}
              {matterId && !matterName && loadingMatter && (
                <li className="party-chip is-muted" data-testid="party-chip-matter-loading">
                  <FaCog className="spinner-cog" aria-hidden="true" />
                  {t("clientDetails.party.loadingMatter")}
                </li>
              )}
              {masterSchedule && (
                <li className="party-chip" data-testid="party-chip-master-schedule">
                  <span className="party-chip-label">{t("clientsTable.masterSchedule")}</span>
                  <span className="party-chip-value">{masterSchedule}</span>
                </li>
              )}
            </ul>
          )}
        </header>

        <div className="party-columns">
          <section className="party-column" aria-labelledby="party-personal-title">
            <div className="party-card-header">
              <h3 id="party-personal-title">{t("clientDetails.party.personalDetails")}</h3>
              <button
                type="button"
                className="party-icon-button party-edit-button"
                onClick={() => setIsContactModalOpen(true)}
                aria-label={t("clientDetails.contact.edit")}
                data-tooltip={t("clientDetails.contact.edit")}
              >
                <FaPen aria-hidden="true" />
              </button>
            </div>
            <div className="party-field-grid is-two-columns">
              <Field
                label={documentLabel}
                value={displayValue(documentValue)}
                testId="party-document"
                flag={(
                  <TaxIdChangeAction
                    party={client}
                    partyId={partyId}
                    features={features}
                    token={tokens?.idToken}
                    author={user?.email}
                    t={t}
                    matterId={matterId}
                    matterName={matterName}
                    onRefresh={refreshPartySilently}
                  />
                )}
              />
              <Field
                label={t("clientsTable.dob")}
                value={details.dob ? formatDisplayDate(details.dob) : "-"}
                testId="party-dob"
                flag={flagFor("dob", t("clientsTable.dob"), false)}
              />
              <Field
                label={t("clientsTable.email")}
                value={displayValue(details.email)}
                testId="party-contact-email"
                flag={flagFor("email", t("clientsTable.email"), true)}
              />
              <Field
                label={t("clientsTable.phone")}
                value={displayValue(formatPhoneForDisplay(details.phone))}
                testId="party-contact-phone"
                flag={flagFor("phone", t("clientsTable.phone"), true)}
              />
            </div>
            <StatusMessage message={statusMessageContact} />
          </section>

          <section className="party-column" aria-labelledby="party-address-title">
            <div className="party-card-header">
              <h3 id="party-address-title">{t("clientDetails.address.title")}</h3>
              {canEditAddress && (
                <button
                  type="button"
                  className="party-icon-button party-edit-button"
                  onClick={() => setIsAddressModalOpen(true)}
                  aria-label={t("clientDetails.address.edit")}
                  data-tooltip={t("clientDetails.address.edit")}
                >
                  <FaPen aria-hidden="true" />
                </button>
              )}
            </div>
            <div className="party-field-grid">
              {[
                ...ADDRESS_DISPLAY_FIELDS,
                ...OPTIONAL_ADDRESS_DISPLAY_FIELDS.filter((field) => String(details[field] ?? "").trim()),
              ].map((field) => (
                <Field
                  key={field}
                  label={t(`clientDetails.address.fields.${field}`)}
                  value={displayAddressField(field, details)}
                  testId={`party-address-${field}`}
                />
              ))}
            </div>
            <StatusMessage message={statusMessageAddress} />
          </section>
        </div>
      </section>

      {compensationRecords.length > 0 && (
        <section className="portal-page-panel party-card" aria-labelledby="party-compensation-title">
          <h3 id="party-compensation-title">{t("clientDetails.compensation.title")}</h3>
          <CompensationTable records={compensationRecords} t={t} />
        </section>
      )}

      {matterDetails?.termination && (
        <section className="portal-page-panel party-card termination-panel" aria-labelledby="party-termination-title">
          <h3 id="party-termination-title">{t("clientsTable.termination.title")}</h3>
          <div className="party-field-grid">
            <Field label={t("clientsTable.termination.reason")} value={displayValue(matterDetails.termination.reason)} />
            <Field
              label={t("clientsTable.termination.date")}
              value={formatDateToDDMMYYYY(
                matterDetails.termination.terminatedDate || matterDetails.termination.closedDate,
              ) || "-"}
            />
          </div>
        </section>
      )}

      {matterDetails?.discontinuance?.available && (
        <section className="portal-page-panel party-card" aria-labelledby="party-discontinuance-title">
          <h3 id="party-discontinuance-title">{t("clientsTable.discontinuance")}</h3>
          <div className="party-field-grid">
            <Field
              label={t("clientsTable.discontinuanceStatus")}
              value={t(
                `clientsTable.discontinuanceStatuses.${(matterDetails.discontinuance.status || "unknown").toLowerCase()}`,
              )}
            />
            <Field
              label={t("clientsTable.questionnaireCompleted")}
              value={matterDetails.discontinuance.questionnaireCompleted
                ? t("coordinatorDetails.yes")
                : t("coordinatorDetails.no")}
            />
          </div>
        </section>
      )}

      {isContactModalOpen && (
        <ContactEditModal
          isOpen
          currentEmail={details.email}
          currentPhone={details.phone}
          onClose={() => setIsContactModalOpen(false)}
          onSave={handleContactSave}
          t={t}
        />
      )}

      {addressEditEnabled && isAddressModalOpen && (
        <AddressEditModal
          isOpen
          onClose={() => setIsAddressModalOpen(false)}
          currentAddress={currentAddress}
          onSave={handleAddressSave}
          t={t}
        />
      )}
    </div>
  );
};

export default ClientDetailsParty;
